import { Button, DialogActions, DialogContent, TextField, Typography } from '@mui/material'
import React from 'react'

import Dialog from '@/app/component/atom/dialog'
import DialogHeader from '@/app/component/atom/dialog-header'
import { cannot } from '@/app/helpers'

import { UserInviteFormValues } from './specs'

type Props = {
  formData: UserInviteFormValues
  onClose: () => void
  onConfirm: () => void
}

const InviteUserConfirmationDialog: React.FC<Props> = ({ formData, onClose, onConfirm }) => {
  const [confirmationText, setConfirmationText] = React.useState('')

  const roleName = getRoleName(formData.role)
  const isConfirmationValid = normalizeText(confirmationText) === normalizeText(roleName)

  const handleClose = () => {
    setConfirmationText('')
    onClose()
  }

  const handleConfirm = () => {
    if (isConfirmationValid) {
      setConfirmationText('')
      onConfirm()
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleConfirm()
    }
  }

  return (
    <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogHeader icon="person_add" title="Confirm Invitation" onClose={handleClose} />
      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2, mt: 2 }}>
          To confirm you want to invite <b>{formData.email}</b>, as a <b>{roleName}</b> to your organization, type "
          <b>{roleName}</b>", then press confirm.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Type the role name to confirm"
          placeholder={roleName}
          value={confirmationText}
          variant="outlined"
          onChange={(e) => setConfirmationText(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" disabled={!isConfirmationValid} onClick={handleConfirm}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export const getRoleName = (role: 'manager' | 'collaborator' | 'cati_agent') => {
  switch (role) {
    case 'manager':
      return 'Manager'
    case 'collaborator':
      return 'Collaborator'
    case 'cati_agent':
      return 'CATI Agent'
    default:
      return cannot(role)
  }
}

// Normalize text for comparison (remove spaces, dashes, make lowercase)
export const normalizeText = (text: string) => text.trim().toLowerCase().replace(/[\s-]/g, '')

export default InviteUserConfirmationDialog
